// Spielseite im Schriftbot-Design. Das Spiel wird direkt groß angezeigt,
// darunter die Weltrangliste. Während einer Runde läuft das Spiel fullscreen
// (Desktop wie Mobil) mit Score-HUD oben; nach dem Game Over poppt die
// Rangliste mit Platzierung und persönlichem Rekord auf.
// Spielen erfordert Login + selbst gewählten Username.
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronLeft, Trophy, LogIn, X, Settings, Play } from "lucide-react";
import { getGame } from "./registry";
import {
  useGameAuth,
  useGameProfile,
  submitScore,
  fetchMyBest,
  fetchTopScores,
  fetchMyRank,
  fetchMyCoins,
  awardCoins,
} from "./gamesFirebase";
import CoinIcon from "./CoinIcon";
import CoinCollectBanner from "./CoinCollectBanner";
import UsernameDialog from "./UsernameDialog";
import { drawSprite } from "./pixelArt";

// Pixel-Vorschaubild des Spiels (gleiche Grafik wie auf der Spielauswahl).
// dim = abgedunkelt, solange man noch nicht spielen darf.
const GamePreview = ({ preview, dim = false }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawSprite(ctx, preview.rows, preview.palette, 0, 0, 16);
  }, [preview]);
  return (
    <canvas
      ref={ref}
      width={320}
      height={160}
      className={`w-full aspect-[2/1] block ${dim ? "opacity-40" : ""}`}
      style={{ imageRendering: "pixelated" }}
      aria-hidden="true"
    />
  );
};

const Leaderboard = ({ topScores, loading, user, compact = false }) => {
  if (loading)
    return <p className="text-sm text-white/40 py-5 text-center">Lade Ranking…</p>;
  if (topScores.length === 0)
    return (
      <p className="text-sm text-white/40 py-5 text-center leading-relaxed">
        Noch keine Einträge.
      </p>
    );
  return (
    <ol className="space-y-1">
      {topScores.map((entry) => {
        const isMe = user && entry.uid === user.uid;
        return (
          <li
            key={entry.uid}
            className={`flex items-center gap-3 px-3 rounded-xl ${
              compact ? "py-1" : "py-1.5"
            } ${
              isMe
                ? "bg-white/[0.08] border border-white/20"
                : "border border-transparent"
            }`}
          >
            <span
              className={`w-5 text-xs font-bold ${
                entry.rank === 1
                  ? "text-amber-400"
                  : entry.rank === 2
                  ? "text-zinc-300"
                  : entry.rank === 3
                  ? "text-amber-700"
                  : "text-white/35"
              }`}
            >
              {entry.rank}
            </span>
            <span className="flex-1 truncate text-white/75 text-sm">
              {entry.name || "Anonym"}
              {isMe && <span className="text-white/40 ml-1">(Du)</span>}
            </span>
            <span className="font-bold text-sm">{entry.score}</span>
          </li>
        );
      })}
    </ol>
  );
};

export default function GamePlayPage() {
  const { gameId } = useParams();
  const game = getGame(gameId);
  const { user, login } = useGameAuth();
  const { username, profileReady, saveUsername } = useGameProfile(user);

  const [playing, setPlaying] = useState(false);
  const [gameKey, setGameKey] = useState(0);
  const [myBest, setMyBest] = useState(null);
  const [lastScore, setLastScore] = useState(null);
  const [isNewBest, setIsNewBest] = useState(false);
  const [myRank, setMyRank] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);
  const [lastCoins, setLastCoins] = useState(0);
  const [topScores, setTopScores] = useState([]);
  const [boardLoading, setBoardLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  // Spielen nur mit Login + Username
  const canPlay = Boolean(user && username);
  const needsUsername = user && profileReady && !username;

  // Laufende Nummer pro Anfrage: verhindert, dass eine langsame ältere
  // Antwort ein frischeres Ergebnis überschreibt (Ursache für die manchmal
  // leere Bestenliste).
  const boardRequestRef = useRef(0);
  const refreshBoard = useCallback(async () => {
    if (!game) return;
    const requestId = ++boardRequestRef.current;
    setBoardLoading(true);
    const scores = await fetchTopScores(game.id, 10);
    if (requestId !== boardRequestRef.current) return;
    // Bei Fehlern (scores === null) die bisherige Liste behalten
    if (scores !== null) setTopScores(scores);
    setBoardLoading(false);
  }, [game]);

  useEffect(() => {
    refreshBoard();
  }, [refreshBoard]);

  useEffect(() => {
    let active = true;
    if (user && game) {
      fetchMyBest(game.id).then((best) => {
        if (active) setMyBest(best);
      });
      fetchMyCoins().then((coins) => {
        if (active) setCoinBalance(coins);
      });
    } else {
      setMyBest(null);
      setCoinBalance(0);
    }
    return () => {
      active = false;
    };
  }, [user, game]);

  // Kein Scrollen, solange das Spiel fullscreen läuft
  useEffect(() => {
    document.body.style.overflow = playing ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [playing]);

  // Solange das Ergebnis-Popup offen ist, keine Tasten an das Spiel
  // durchlassen (die Spiele hören global auf keydown).
  useEffect(() => {
    if (!showResults) return;
    const block = (e) => e.stopPropagation();
    window.addEventListener("keydown", block, true);
    return () => window.removeEventListener("keydown", block, true);
  }, [showResults]);

  const handleStart = useCallback(() => {
    setPlaying(true);
    setShowResults(false);
    setIsNewBest(false);
  }, []);

  const handleGameOver = useCallback(
    async (score, coins = 0) => {
      setLastScore(score);
      setLastCoins(coins);
      setIsNewBest(false);
      setMyRank(null);
      setShowResults(true);
      if (!game) return;
      if (user && coins > 0) {
        const coinResult = await awardCoins(coins);
        if (coinResult) setCoinBalance(coinResult.coins);
      }
      let best = myBest;
      if (user) {
        const result = await submitScore(game.id, score, username);
        best = result.best;
        setMyBest(result.best);
        if (result.isNewBest) setIsNewBest(true);
      } else if (myBest === null || score > myBest) {
        best = score;
        setMyBest(score);
      }
      refreshBoard();
      if (user && best !== null) {
        const rank = await fetchMyRank(game.id, best);
        setMyRank(rank);
      }
    },
    [game, user, username, myBest, refreshBoard]
  );

  // Start-Button: Spiel im Fullscreen mounten, startet sofort (autoStart)
  const startGame = () => {
    setShowResults(false);
    setIsNewBest(false);
    setGameKey((k) => k + 1);
    setPlaying(true);
  };

  // Fullscreen verlassen: Spiel unmounten und Seite zeigen
  const exitGame = () => {
    setPlaying(false);
    setShowResults(false);
  };

  // Nach dem Game Over direkt nochmal: Popup zu, frisches Spiel im Fullscreen
  const playAgain = () => {
    setShowResults(false);
    setGameKey((k) => k + 1);
  };

  if (!game) return <Navigate to="/games" replace />;
  const GameComponent = game.component;

  return (
    <div className="min-h-screen bg-[#0b0c0f] text-white selection:bg-white selection:text-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header — nur sichtbar, wenn kein Spiel läuft */}
        {!playing && (
          <header className="flex items-center justify-between gap-4 mb-6 sm:mb-8">
            <Link
              to="/games"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              <ChevronLeft size={16} /> Alle Spiele
            </Link>
            {user ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-white/55 hidden sm:inline">
                  {username || ""}
                </span>
                <button
                  onClick={() => setShowSettings(true)}
                  aria-label="Einstellungen"
                  className="glass-accent inline-flex items-center justify-center w-9 h-9 rounded-full"
                >
                  <Settings size={15} />
                </button>
              </div>
            ) : (
              <button
                onClick={login}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-zinc-900 text-sm font-bold hover:opacity-90 transition-opacity"
              >
                <LogIn size={14} /> Anmelden
              </button>
            )}
          </header>
        )}

        {!playing && (
          <div className="flex flex-col gap-1 mb-5">
            <h1 className="text-2xl font-bold tracking-tight">{game.name}</h1>
            <p className="text-xs text-white/55 font-medium uppercase tracking-widest">
              {game.tagline}
            </p>
            <CoinCollectBanner coins={coinBalance} showCollected={Boolean(user)} />
          </div>
        )}

        {/* Vorschau + Start-Button — das Spiel selbst läuft nur im
            Fullscreen-Overlay weiter unten */}
        {!playing && (
          <div className="relative rounded-xl overflow-hidden">
            <GamePreview preview={game.preview} dim={!canPlay} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center bg-black/25">
              {canPlay ? (
                <button
                  onClick={startGame}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-zinc-900 text-sm font-bold hover:opacity-90 transition-opacity"
                >
                  <Play size={16} /> Start
                </button>
              ) : (
                <>
                  <p className="text-sm text-white/80 font-medium max-w-xs leading-relaxed">
                    {user
                      ? "Wähle einen Username, um zu spielen."
                      : "Melde dich an und wähle einen Username, um zu spielen."}
                  </p>
                  {user ? (
                    <button
                      onClick={() => setShowSettings(true)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-zinc-900 text-sm font-bold hover:opacity-90 transition-opacity"
                    >
                      Username wählen
                    </button>
                  ) : (
                    <button
                      onClick={login}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-zinc-900 text-sm font-bold hover:opacity-90 transition-opacity"
                    >
                      <LogIn size={14} /> Anmelden
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Fullscreen-Spiel: füllt den Bildschirm in Breite und Höhe */}
        {playing && (
          <div className="fixed inset-0 z-50 bg-[#0b0c0f] flex flex-col">
            <div className="h-14 shrink-0 flex items-center justify-between px-4 sm:px-6">
              <div className="flex items-baseline gap-4">
                <span className="text-sm font-bold tracking-tight">
                  {game.name}
                </span>
                <span className="text-[11px] uppercase font-bold tracking-widest text-white/45">
                  Best {myBest !== null ? myBest : "–"}
                </span>
              </div>
              <button
                onClick={exitGame}
                aria-label="Spiel beenden"
                className="glass-accent w-9 h-9 flex items-center justify-center rounded-full"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <GameComponent
                key={gameKey}
                autoStart
                onStart={handleStart}
                onGameOver={handleGameOver}
              />
            </div>
          </div>
        )}

        {/* Weltrangliste unter der Vorschau */}
        {!playing && (
          <aside className="mt-4 bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5">
            <h2 className="inline-flex items-center gap-2 text-[11px] uppercase font-bold tracking-widest text-white/80 mb-4">
              <Trophy size={14} /> Weltrangliste
            </h2>

            <div className="max-h-96 overflow-y-auto">
              <Leaderboard
                topScores={topScores}
                loading={boardLoading}
                user={user}
              />
            </div>
          </aside>
        )}
      </div>

      {/* Ergebnis-Popup nach dem Game Over: Platzierung, Rekord, Rangliste */}
      {showResults && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-sm max-h-[85dvh] overflow-y-auto bg-[#15171c] border border-white/10 rounded-2xl p-4">
            <p className="text-3xl font-semibold tracking-tight">{lastScore}</p>
            {isNewBest && (
              <p className="text-xs text-emerald-400 mt-0.5">Neuer Rekord</p>
            )}

            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-white/45">
                  Weltrang
                </p>
                <p className="text-sm font-semibold mt-0.5">
                  {myRank !== null ? `#${myRank}` : "…"}
                </p>
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-white/45">
                  Rekord
                </p>
                <p className="text-sm font-semibold mt-0.5">
                  {myBest !== null ? myBest : "–"}
                </p>
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-white/45">
                  Münzen
                </p>
                <p className="inline-flex items-center gap-1 text-sm font-semibold mt-0.5">
                  <CoinIcon size={12} />
                  {user ? coinBalance : lastCoins}
                  {lastCoins > 0 && (
                    <span className="text-amber-300">+{lastCoins}</span>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-4 border-t border-white/10 pt-3">
              <h2 className="text-[10px] uppercase tracking-wider text-white/45 mb-2">
                Weltrangliste
              </h2>
              <div className="max-h-56 overflow-y-auto">
                <Leaderboard
                  topScores={topScores}
                  loading={boardLoading}
                  user={user}
                  compact
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={playAgain}
                className="flex-1 py-2.5 rounded-full bg-white text-zinc-900 text-xs font-semibold hover:opacity-90 transition-opacity"
              >
                Nochmal
              </button>
              <button
                onClick={exitGame}
                className="flex-1 py-2.5 rounded-full glass-accent text-xs font-semibold"
              >
                Beenden
              </button>
            </div>
          </div>
        </div>
      )}

      {(needsUsername || showSettings) && (
        <UsernameDialog
          initial={username}
          onSave={saveUsername}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
